import { StoneType } from "@/types/stone-type/stone-type.types";
import { Column } from "@/components/ui/data-table/types";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Pencil, Archive, ArchiveRestore } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatNumberDynamic } from "@/utils/numberFormatter";

export const useStoneTypeColumns = (
  t: (key: string) => string,
  onEdit: (stoneType: StoneType) => void,
  onArchiveAction: (stoneType: StoneType) => void
): Column<StoneType>[] => {
  const { claims } = useAuth();
  const isAssimagraUser = claims?.role === "assimagra";
  
  const baseColumns: Column<StoneType>[] = [
    {
      header: t("stoneTypes.table.name"),
      accessorKey: "name",
      sortable: true,
      className: "min-w-[180px]",
    },
    {
      header: t("stoneTypes.table.density"),
      accessorKey: "dens_pedra",
      sortable: true,
      className: "min-w-[120px]",
      cell: (_: string, row: StoneType) => (
        <div>
          {row.dens_pedra?.value !== undefined
            ? formatNumberDynamic(row.dens_pedra.value)
            : "-"}
        </div>
      ),
    },
    {
      header: t("stoneTypes.table.fe_extracao"),
      accessorKey: "fe_extracao",
      sortable: true,
      className: "min-w-[120px]",
      cell: (_: string, row: StoneType) => (
        <div>
          {row.fe_extracao?.value !== undefined
            ? formatNumberDynamic(row.fe_extracao.value)
            : "-"}
        </div>
      ),
    },
    {
      header: t("stoneTypes.table.fh_extracao"),
      accessorKey: "fh_extracao",
      sortable: true,
      className: "min-w-[120px]",
      cell: (_: string, row: StoneType) => (
        <div>
          {row.fh_extracao?.value !== undefined
            ? formatNumberDynamic(row.fh_extracao.value)
            : "-"}
        </div>
      ),
    },
  ];

  const actionsColumn: Column<StoneType> = {
    header: t("stoneTypes.table.actions"),
    accessorKey: "id",
    sortable: false,
    className: "w-[100px]",
    cell: (_: string, row: StoneType) => (
      <div className="flex items-center gap-1">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onEdit(row)}
                className="h-8 w-8 hover:bg-gray-100"
              >
                <Pencil className="h-4 w-4 text-gray-500" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">
              <p>{t("stoneTypes.actions.edit")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onArchiveAction(row)}
                className="h-8 w-8 hover:bg-gray-100"
              >
                {row.isArchived ? (
                  <ArchiveRestore className="h-4 w-4 text-gray-500" />
                ) : (
                  <Archive className="h-4 w-4 text-gray-500" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">
              <p>
                {row.isArchived
                  ? t("stoneTypes.actions.restore")
                  : t("stoneTypes.actions.archive")}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
  };

  return isAssimagraUser ? baseColumns : [...baseColumns, actionsColumn];
};
